import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, AlertCircle, ArrowLeft, Loader2, ExternalLink } from "lucide-react";

const T = {
  cream:    "#F7F4EE",
  ink:      "#0A192F",
  inkFaint: "#5A6478",
  line:     "#E5E1D8",
  lineSoft: "rgba(10,25,47,0.16)",
  coral:    "#E24E1B",
  coralDark:"#B33E15",
  brass:    "#C8A464",
  sage:     "#4F7A5B",
};
const FONT_DISPLAY = "'Fraunces', 'Playfair Display', Georgia, serif";
const FONT_MONO    = "'JetBrains Mono', 'IBM Plex Mono', monospace";
const ENGINES = [
  { key: "chatgpt",    label: "ChatGPT" },
  { key: "gemini",     label: "Gemini" },
  { key: "claude",     label: "Claude" },
  { key: "perplexity", label: "Perplexity" },
];

/**
 * /ai-seo-audit-report/:id — public shareable report page.
 * Polls GET /api/public/ai-seo-audit/{id} every 4s until status=complete.
 */
export default function AiSeoReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    const tick = async () => {
      try {
        const { data: body } = await api.get(`/public/ai-seo-audit/${id}`);
        if (!active) return;
        setData(body);
        if (body.status !== "complete" && body.status !== "failed") {
          setTimeout(tick, 4000);
        }
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || e.message);
      }
    };
    tick();
    return () => { active = false; };
  }, [id]);

  if (err) {
    return (
      <Shell>
        <div data-testid="ai-seo-report-error" style={{ padding: 40, textAlign: "center" }}>
          <AlertCircle size={48} color={T.coral} style={{ margin: "0 auto 16px" }} />
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, margin: 0 }}>Report not found</h2>
          <p style={{ color: T.inkFaint, marginTop: 10 }}>{err}</p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return <Shell><Loading label="Loading report…" /></Shell>;
  }

  if (data.status === "failed") {
    return (
      <Shell>
        <div style={{ padding: 40, textAlign: "center" }} data-testid="ai-seo-report-failed">
          <AlertCircle size={48} color={T.coral} style={{ margin: "0 auto 16px" }} />
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, margin: 0 }}>Audit failed</h2>
          <p style={{ color: T.inkFaint, marginTop: 10, maxWidth: 500, margin: "10px auto" }}>{data.error || "Please retry."}</p>
        </div>
      </Shell>
    );
  }

  if (data.status !== "complete") {
    return (
      <Shell>
        <Loading label="Querying ChatGPT, Gemini, Claude, Perplexity… hang tight (~2 min)" />
        <div style={{ textAlign: "center", fontSize: 12, color: T.inkFaint, fontFamily: FONT_MONO, marginTop: 20 }}>
          Status: <strong>{data.status.toUpperCase()}</strong> · {data.prompts?.length || "generating"} prompts
        </div>
      </Shell>
    );
  }

  const biz = data.business_name;
  const favicon = `https://www.google.com/s2/favicons?sz=64&domain=${data.domain}`;
  const score = data.visibility_score || 0;
  const scoreColor = score >= 60 ? T.sage : score >= 30 ? T.brass : T.coral;
  const sov = data.share_of_voice || [];
  const prompts = data.prompt_results || [];
  const citations = data.citations || {};

  return (
    <Shell>
      <div data-testid="ai-seo-report-page" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 28px 80px" }}>
        {/* HEADER */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <img src={favicon} alt="" width={40} height={40} style={{ border: `1px solid ${T.line}` }} />
            <div style={{ flex: 1 }}>
              <h1 data-testid="report-business-name" style={{ fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 500, letterSpacing: "-0.02em", margin: 0, lineHeight: 1.1 }}>
                {biz}
              </h1>
              <div style={{ fontSize: 13, color: T.inkFaint, fontFamily: FONT_MONO, marginTop: 4 }}>
                <a href={data.website} target="_blank" rel="noreferrer" style={{ color: T.inkFaint, textDecoration: "none", borderBottom: `1px solid ${T.lineSoft}` }}>
                  {data.website.replace(/^https?:\/\//, "")}
                </a>
                {" · "}Snapshot {new Date(data.completed_at || data.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* HERO SCORE */}
        <section style={{ background: "#fff", border: `1px solid ${T.lineSoft}`, padding: "40px 32px", marginBottom: 32 }}>
          <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.inkFaint, fontWeight: 600 }}>
            AI Search Visibility Score
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 24, marginTop: 10 }}>
            <div data-testid="visibility-score" style={{ fontFamily: FONT_DISPLAY, fontSize: 120, fontWeight: 700, color: scoreColor, letterSpacing: "-0.04em", lineHeight: 1 }}>
              {score}
            </div>
            <div style={{ fontSize: 20, color: T.inkFaint }}>/ 100</div>
          </div>
          <p style={{ fontSize: 15, color: T.inkFaint, marginTop: 16, maxWidth: 640, lineHeight: 1.55 }}>
            Your brand was found in <strong style={{ color: T.ink }}>{data.visibility_cells?.cited || 0} out of {data.visibility_cells?.total || 0}</strong> AI search cells across {data.engines_used?.length || 4} engines ({(data.engines_used || []).map((e) => e.charAt(0).toUpperCase() + e.slice(1)).join(", ")}).
          </p>
        </section>

        {/* PROMPT RESULTS */}
        <section style={{ background: "#fff", border: `1px solid ${T.lineSoft}`, padding: "32px", marginBottom: 32 }} data-testid="prompt-results-section">
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 500, letterSpacing: "-0.01em", margin: "0 0 8px" }}>Prompt results</h2>
          <p style={{ fontSize: 13, color: T.inkFaint, margin: "0 0 24px" }}>
            Top brand each engine named for every query we ran.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.ink}`, textAlign: "left" }}>
                  <th style={{ padding: "10px 8px", fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint }}>Prompt</th>
                  {ENGINES.map((e) => (
                    <th key={e.key} style={{ padding: "10px 8px", fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.inkFaint, textAlign: "center", minWidth: 110 }}>
                      {e.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prompts.map((row, i) => (
                  <tr key={i} data-testid={`prompt-row-${i}`} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                    <td style={{ padding: "14px 8px", maxWidth: 340 }}>{row.prompt}</td>
                    {ENGINES.map((e) => {
                      const cell = row.per_engine?.[e.key];
                      const top = cell?.top || "—";
                      const ours = cell?.has_our_brand;
                      return (
                        <td key={e.key} style={{ padding: "14px 8px", textAlign: "center", fontSize: 12 }}>
                          <div style={{ color: ours ? T.sage : T.ink, fontWeight: ours ? 700 : 400 }}>
                            {top}
                            {ours && <CheckCircle2 size={12} color={T.sage} style={{ display: "inline", marginLeft: 4, marginBottom: -1 }} />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* SHARE OF VOICE */}
        <section style={{ background: "#fff", border: `1px solid ${T.lineSoft}`, padding: "32px", marginBottom: 32 }} data-testid="sov-section">
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 500, letterSpacing: "-0.01em", margin: "0 0 8px" }}>Brands dominating your AI search space</h2>
          <p style={{ fontSize: 13, color: T.inkFaint, margin: "0 0 24px" }}>
            Which brands appear most often across the {data.visibility_cells?.total || 0} AI responses — and how yours ranks.
          </p>
          <div style={{ display: "grid", gap: 8 }}>
            {sov.map((row, i) => {
              const isUs = row.brand === biz;
              const maxMentions = Math.max(1, ...sov.map((s) => s.mentions));
              const pct = Math.max(5, (row.mentions / maxMentions) * 100);
              return (
                <div key={i} data-testid={`sov-row-${i}`} style={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 12, alignItems: "center" }}>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 13, color: isUs ? T.coral : T.inkFaint, fontWeight: isUs ? 700 : 400 }}>
                    {i + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: isUs ? 700 : 500, color: isUs ? T.coral : T.ink, marginBottom: 4 }}>
                      {row.brand}{isUs && <span style={{ fontSize: 10, fontFamily: FONT_MONO, color: T.coral, marginLeft: 6, padding: "1px 6px", border: `1px solid ${T.coral}` }}>YOU</span>}
                    </div>
                    <div style={{ background: T.lineSoft, height: 6 }}>
                      <div style={{ background: isUs ? T.coral : T.ink, height: "100%", width: `${pct}%` }} />
                    </div>
                  </div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, color: isUs ? T.coral : T.ink }}>
                    {row.mentions} / {data.visibility_cells?.total || 0}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CITATIONS */}
        <section style={{ background: "#fff", border: `1px solid ${T.lineSoft}`, padding: "32px", marginBottom: 32 }} data-testid="citations-section">
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 500, letterSpacing: "-0.01em", margin: "0 0 8px" }}>Sources cited by AI</h2>
          <p style={{ fontSize: 13, color: T.inkFaint, margin: "0 0 24px" }}>
            Every website AI referenced while answering about your industry.
          </p>

          {/* 3 big stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${T.lineSoft}` }}>
            <StatCell label="Your site cited" value={`${citations.citation_pct || 0}%`} sub={`${citations.your_site_cited || 0} of ${citations.total_cited || 0} citations`} />
            <StatCell label="Cited pages"     value={citations.unique_pages || 0}     sub="Unique URLs cited across answers" />
            <StatCell label="Cited domains"   value={citations.unique_domains || 0}   sub="Unique websites cited" />
          </div>

          {/* Top cited pages */}
          <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.inkFaint, fontWeight: 600, marginBottom: 14 }}>
            Top cited pages
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {(citations.top_cited_pages || []).map((p, i) => (
              <div key={i} data-testid={`cited-page-${i}`} style={{ display: "grid", gridTemplateColumns: "20px 1fr auto auto", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.lineSoft}` }}>
                <img src={`https://www.google.com/s2/favicons?sz=32&domain=${p.domain}`} alt="" width={16} height={16} />
                <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: T.ink, textDecoration: "none" }}>
                  <div style={{ fontWeight: 500 }}>{p.title || p.domain || p.url}</div>
                  <div style={{ fontSize: 11, color: T.inkFaint, fontFamily: FONT_MONO, marginTop: 2 }}>{p.domain}</div>
                </a>
                <div style={{ fontSize: 11, color: T.inkFaint, fontFamily: FONT_MONO }}>{p.platforms.join(" · ")}</div>
                <div style={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700 }}>{p.count}×</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{ background: T.ink, color: T.cream, padding: "40px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.brass, fontWeight: 700 }}>
            Want to improve this score?
          </div>
          <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 500, letterSpacing: "-0.01em", margin: "12px 0 20px" }}>
            Let's get your brand cited across every AI engine.
          </h3>
          <Link to="/book" style={{ background: T.coral, color: T.cream, padding: "14px 24px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            Book a strategy call <ExternalLink size={14} />
          </Link>
        </section>
      </div>
    </Shell>
  );
}


function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: T.cream, color: T.ink, fontFamily: "Inter, -apple-system, sans-serif" }}>
      <nav style={{ padding: "20px 28px", borderBottom: `1px solid ${T.lineSoft}`, background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 700, color: T.ink, textDecoration: "none" }}>Clients OnRamp</Link>
          <Link to="/free-ai-seo-audit" style={{ fontSize: 13, color: T.inkFaint, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ArrowLeft size={14} /> run another audit
          </Link>
        </div>
      </nav>
      {children}
    </div>
  );
}

function Loading({ label }) {
  return (
    <div style={{ padding: "80px 28px", textAlign: "center" }} data-testid="ai-seo-report-loading">
      <Loader2 size={40} color={T.coral} style={{ margin: "0 auto 16px", animation: "spin 1.1s linear infinite" }} />
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function StatCell({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: T.inkFaint, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: 700, letterSpacing: "-0.02em", color: T.coral, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.inkFaint, marginTop: 6 }}>{sub}</div>
    </div>
  );
}
